import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { Util } from 'src/shared/util/util';

import { TaxFilingDetailsPage } from 'src/pages/finance/tax-filing-details/tax-filing-details';
import { JurisdictionDetailsPage } from 'src/pages/finance/jurisdiction-details/jurisdiction-details';

import {
  ACTIVE_TAX_STATUSES,
  AddressTax,
  StatusTone,
  TONE_BG,
  TONE_COLOR,
  TaxComplianceCheck,
  TaxesService,
  capitalize,
  compareDueDates,
  filingEstimateNote,
  formatCents,
  formatShortDate,
  frequencyLabel,
  isTaxOverdue,
  taxStatusLabel,
  taxStatusTone,
  translateTax,
} from 'src/shared/services/taxes.service';

interface TaxRowVM {
  id: number;
  name: string;
  categoryLine: string;
  statusLabel: string;
  statusTone: StatusTone;
  nextDueDisplay: string;
  nextDueOverdue: boolean;
}

interface SuggestionVM {
  id: number;
  name: string;
  rateLabel: string;
  reason: string;
}

interface JurisdictionRowVM {
  key: string;
  name: string;
  agency: string;
}

interface HeroVM {
  tone: StatusTone;
  label: string;
  title: string;
  subtitle: string;
  amount: string;
}

@Component({
  templateUrl: 'property-tax-details.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PropertyTaxDetailsPage implements OnInit {

  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private windowService = inject(WindowService);
  private taxesService = inject(TaxesService);
  private util = inject(Util);
  private demoBlock = inject(DemoBlockService);

  loaded = false;
  loadFailed = false;
  isRightSideContent = true;
  propertyId: number;
  propertyName = '';
  propertyColor = '';
  taxes: TaxRowVM[] = [];
  suggestions: SuggestionVM[] = [];
  emptyText = 'No taxes yet.';
  reportNote = '';
  jurisdictions: JurisdictionRowVM[] = [];
  hero: HeroVM | null = null;
  readonly toneColor = TONE_COLOR;
  readonly toneBg = TONE_BG;
  readonly boundRetry = () => this.loadData();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const dialogParams = await this.storage.retrieve('dialog-params');
    this.propertyId = this.navCtrl.getParam('propertyId') ?? dialogParams?.propertyId;
    this.propertyName = this.navCtrl.getParam('propertyName') ?? dialogParams?.propertyName ?? '';
    this.propertyColor = this.navCtrl.getParam('propertyColor') ?? dialogParams?.propertyColor ?? '#72CB9B';
    this.rightSidePanelService.setDialogPageTitle('Taxes');
    await this.loadData();
  }

  // On mobile the page stays alive under the pages it opens, so refresh on the way back.
  async ionViewWillEnter() {
    if (this.loaded) {
      await this.loadData();
    }
  }

  private async loadData() {
    try {
      const [allTaxes, check] = await Promise.all([
        this.taxesService.getAddressTaxes([this.propertyId]),
        // undefined marks a failed request, so it isn't read as "never checked" (null).
        this.taxesService.getLatestComplianceCheck(this.propertyId).catch((): undefined => undefined),
      ]);
      const activeTaxes = allTaxes.filter(tax => ACTIVE_TAX_STATUSES.includes(tax.status));
      const suggestedTaxes = allTaxes.filter(tax => tax.status === 'suggested');

      this.taxes = activeTaxes.map(tax => this.buildTaxRowVM(tax));
      this.suggestions = suggestedTaxes.map(tax => this.buildSuggestionVM(tax));
      this.emptyText = this.suggestions.length > 0 ? 'No taxes yet. Review the suggestions below.' : 'No taxes yet.';
      this.reportNote = this.buildReportNote(check, allTaxes.length > 0);
      this.jurisdictions = this.buildJurisdictions(allTaxes.filter(tax => tax.status !== 'dismissed'));
      this.hero = this.buildHero(activeTaxes);
      this.loadFailed = false;
    } catch {
      // An empty list here would read as "no taxes" and offer Add Tax, so the page
      // shows the failure with a retry instead.
      this.loadFailed = true;
    }
    this.loaded = true;
  }

  private buildReportNote(check: TaxComplianceCheck | null | undefined, hasTaxes: boolean): string {
    if (check === undefined) {
      return translateTax('Could not load the compliance report for this property.');
    }
    if (!check) {
      return hasTaxes ? '' : translateTax('The compliance report hasn\'t checked this property yet.');
    }
    if (check.status === 'pending' || check.status === 'processing') {
      return translateTax('The compliance report is checking this property for taxes.');
    }
    if (check.status === 'failed') {
      return check.error_message
        ? translateTax('The last compliance report failed: %error%', { error: check.error_message })
        : translateTax('The last compliance report failed.');
    }
    return '';
  }

  // The soonest unfiled filing across the property's adopted taxes, surfaced as
  // a hero so the next thing to do is the first thing seen.
  private buildHero(activeTaxes: AddressTax[]): HeroVM | null {
    const withFilings = activeTaxes
      .filter(tax => tax.next_filing)
      .sort((a, b) => compareDueDates(a.next_filing.due_date, b.next_filing.due_date));
    if (withFilings.length === 0) { return null; }
    const tax = withFilings[0];
    const filing = tax.next_filing;
    const overdue = filing.status === 'overdue';
    const cents = filing.calculated_amount || 0;
    const estimateNote = filingEstimateNote(filing);
    return {
      tone: overdue ? 'bad' : 'warn',
      label: overdue ? 'Filing overdue' : 'Next filing due',
      title: `${tax.name} · ${formatShortDate(filing.due_date) || translateTax('Deadline not set')}`,
      subtitle: overdue
        ? translateTax('Past due — file as soon as possible')
        : (tax.frequency ? translateTax('%frequency% filing', { frequency: frequencyLabel(tax.frequency) }) : translateTax('Upcoming filing')),
      amount: [
        cents > 0 ? translateTax('Est. %amount% owed', { amount: formatCents(cents) }) : translateTax('No amount owed this period'),
        estimateNote,
      ].filter(Boolean).join(' · '),
    };
  }

  private buildTaxRowVM(tax: AddressTax): TaxRowVM {
    return {
      id: tax.id,
      name: tax.name,
      categoryLine: [capitalize(tax.category), tax.jurisdiction || tax.agency].filter(Boolean).join(' · '),
      statusLabel: taxStatusLabel(tax),
      statusTone: taxStatusTone(tax),
      nextDueDisplay: this.nextDueDisplay(tax),
      nextDueOverdue: isTaxOverdue(tax),
    };
  }

  private nextDueDisplay(tax: AddressTax): string {
    if (tax.next_filing && !tax.next_filing.due_date) {
      return translateTax('Deadline not set');
    }
    if (tax.next_filing) {
      const date = formatShortDate(tax.next_filing.due_date);
      return tax.next_filing.status === 'overdue'
        ? translateTax('Filing overdue since %date%', { date })
        : translateTax('Next filing due %date%', { date });
    }
    if (tax.status === 'pending' || tax.status === 'action_needed') {
      return translateTax('Register with the agency to start filing');
    }
    return translateTax('No upcoming filing');
  }

  private buildSuggestionVM(tax: AddressTax): SuggestionVM {
    return {
      id: tax.id,
      name: tax.name,
      rateLabel: tax.rate !== null && tax.rate !== undefined ? `${tax.rate}%` : '',
      reason: tax.jurisdiction
        ? translateTax('Suggested by the compliance report for %jurisdiction%', { jurisdiction: tax.jurisdiction })
        : translateTax('Suggested by the compliance report'),
    };
  }

  private buildJurisdictions(taxes: AddressTax[]): JurisdictionRowVM[] {
    const byKey = new Map<string, JurisdictionRowVM>();
    taxes.forEach(tax => {
      const key = tax.jurisdiction || tax.agency;
      if (!key || byKey.has(key)) { return; }
      byKey.set(key, { key, name: key, agency: tax.agency || '' });
    });
    return Array.from(byKey.values());
  }

  async addSuggestedTax(suggestion: SuggestionVM) {
    if (this.demoBlock.block()) { return; }
    try {
      await this.taxesService.updateTax(suggestion.id, { status: 'pending' });
      await this.storage.save('didMakePropertyTaxChanges', true);
      await this.loadData();
    } catch (error) {
      this.util.showError(error?.error?.message || error?.message || 'Failed to add tax');
    }
  }

  async dismissSuggestion(suggestion: SuggestionVM) {
    if (this.demoBlock.block()) { return; }
    try {
      await this.taxesService.updateTax(suggestion.id, { status: 'dismissed' });
      await this.storage.save('didMakePropertyTaxChanges', true);
      await this.loadData();
    } catch (error) {
      this.util.showError(error?.error?.message || error?.message || 'Failed to dismiss suggestion');
    }
  }

  addTax() {
    if (this.demoBlock.block()) { return; }
    // The panel merges params with the previous page's, so clear a taxId left by the tax page.
    const params = { propertyId: this.propertyId, taxId: null };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('add-edit-property-tax', params);
    } else {
      this.navCtrl.navigateForward('add-edit-property-tax', params);
    }
  }

  openTaxDetails(tax: TaxRowVM) {
    const params = { propertyId: this.propertyId, taxId: tax.id, propertyName: this.propertyName, propertyColor: this.propertyColor };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('tax-filing-details', params, TaxFilingDetailsPage);
    } else {
      this.navCtrl.navigateForward('tax-filing-details', params);
    }
  }

  openJurisdictionDetails(jurisdiction: JurisdictionRowVM) {
    const params = { propertyId: this.propertyId, jurisdictionKey: jurisdiction.key };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('jurisdiction-details', params, JurisdictionDetailsPage);
    } else {
      this.navCtrl.navigateForward('jurisdiction-details', params);
    }
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
